<script setup lang="ts">
  import { computed, watch } from 'vue'
  import { useFormField } from '@/composables/useFormField'
  import { useThemeColor } from '@/composables/useThemeColorV3'
  import { useTranslation } from '@/composables/useTranslation'
  import { useFieldValidation } from '@/composables/useQuestionnaire'

  const props = defineProps({
    field: {
      type: Object,
      required: true
    },
    modelValue: {
      type: [String, Boolean],
      default: null
    },
    isPreviewMode: {
      type: Boolean,
      default: false
    }
  })

  const emit = defineEmits(['update:modelValue', 'validation-error'])

  const { selectedValue } = useFormField(props)
  const themeColor = computed(() => useThemeColor().value)

  const { t } = useTranslation({
    en: {
      noAnswer: 'No answer'
    },
    de: {
      noAnswer: 'keine Angabe'
    }
  })

  // Use the validation composable
  const { validationError, handleInputChange } = useFieldValidation(props, emit)

  const options = computed(() => {
    if (props.field.type === 'yesno') {
      return [
        { value: true, label: 'Ja' },
        { value: false, label: 'Nein' }
      ]
    }
    return props.field.choices.map((choice: any) => ({
      value: choice,
      label: choice === '' ? t('noAnswer') : choice
    }))
  })

  const isSelected = (optionValue: any) => {
    return selectedValue.value === optionValue
  }

  const handleRadioClick = (optionValue: any) => {
    if (props.isPreviewMode) return
    selectedValue.value = optionValue
    handleInputChange(optionValue)
  }

  // Ensure the correct option is selected when the component mounts or modelValue changes
  watch(
    () => props.modelValue,
    (newValue) => {
      selectedValue.value = newValue
    },
    { immediate: true }
  )
</script>

<template>
  <div class="flex flex-col gap-2">
    <div
      v-for="(option, index) in options"
      :key="option.value"
      class="flex items-center gap-2 rounded-lg p-2 transition-colors"
      :class="[
        isSelected(option.value) ? 'bg-gray-50' : 'bg-white',
        isPreviewMode ? 'cursor-default opacity-60' : 'cursor-pointer'
      ]"
    >
      <div class="relative">
        <input
          :id="option.value"
          v-model="selectedValue"
          type="radio"
          :value="option.value"
          :disabled="isPreviewMode"
          class="sr-only"
          @change="handleRadioClick(selectedValue)"
        />
        <!-- Custom radio button -->
        <div
          class="flex h-4 w-4 items-center justify-center rounded-full border transition-colors"
          :class="[
            isSelected(option.value)
              ? `bg-${themeColor}-600 border-${themeColor}-600`
              : 'border-gray-300 bg-white',
            isPreviewMode ? 'cursor-default' : 'cursor-pointer'
          ]"
          :data-cy="`RadioButtonV3Radio-${field.uuid}-${index}`"
          @click="handleRadioClick(option.value)"
        >
          <div
            v-if="isSelected(option.value)"
            class="h-2 w-2 rounded-full bg-white"
          ></div>
        </div>
      </div>
      <span
        class="flex-1 text-sm font-medium text-gray-900"
        :class="isPreviewMode ? 'cursor-default' : 'cursor-pointer'"
        @click="handleRadioClick(option.value)"
      >
        {{ option.label }}
      </span>
    </div>
    <div
      v-if="validationError"
      class="mt-1 text-sm text-red-600"
    >
      {{ validationError }}
    </div>
  </div>
</template>

<style scoped>
  label {
    display: flex;
    align-items: center;
    padding: 5px 1rem 5px 5px;
    border-radius: 3px;
    margin: 5px 0;
    cursor: pointer;
  }
</style>
